import React from 'react';
import { connect } from 'react-redux';
import { getActivitiesSettings } from 'helpers/objectiveSettings';
import { generateCalloutComponents } from 'helpers/componentGenerators';
import { convertToDisplayValue } from 'helpers/strings';
import { shouldShowTotalActivities } from 'helpers/data';
import { get } from 'lodash';

import { fetchMeta as fetchEntityMeta } from 'services/entities/duck';
import { fetchIOChannels } from 'services/siteDirect/duck';
import { fetchChannel, fetchChannelCallouts, requestChannelUpsell, showedChannelUpsell } from 'services/channel/duck';
import { fetchActivities } from 'services/activities/duck';
import { setObjectiveShadow } from 'services/config/duck';

// Components and Containers
import LayerThreeDashboardComponent from 'containers/dashboards/DashboardBaseComponent';
import { Grid, Row } from 'react-bootstrap';
import { NoChannelDataTile } from 'components/molecules/ErrorTile';
import RenderingTile from 'components/molecules/RenderingTile';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import ExportPDF from 'containers/buttons/ExportPDF';
import ChannelUpsell from 'components/molecules/ChannelUpsell';
import { channelMap as SiteDirectContainerMap } from 'containers/dashboards/siteDirect/index';
import { orderedChannels } from 'constants/siteDirect';
import CreateNewNote from 'containers/buttons/CreateNewNote';
import ViewEntity from 'containers/buttons/ViewEntity';
import Note from 'containers/annotations/NoteContainer';
import ObjectiveSwitcher from 'components/molecules/ObjectiveSwitcher';

// Callouts
import CallOutValueWithTreemap from 'components/molecules/CallOutValueWithTreemap';

// Charts
import ChannelStackedChart from 'components/molecules/charts/ChannelStackedChart';
import TwoAxisBarWithLineChart from 'components/molecules/charts/TwoAxisBarWithLineChart';
import ActivitiesStackedAreaChart from 'components/molecules/charts/ActivitiesStackedAreaChart';

// Tables
import CommonSummaryTable from 'components/molecules/tables/CommonSummaryTable';

class Dashboard extends LayerThreeDashboardComponent {
    constructor(props) {
        super(props, 'site');
    }

    makeRequestsForData() {
        const entityId = this.getCurrentEntityId();
        const channel = this.getCurrentChannel();

        const payload = this.createPayload(entityId, channel);

        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entities.meta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }

        this.props.fetchIOChannels(payload);
        this.props.fetchChannel(payload);
        this.props.fetchChannelCallouts(payload);
        this.props.fetchActivities(payload);
    }

    render() {
        const stub = `l3-${this.getCurrentChannel()}`;
        const {
            backfillBuckets,
            xAxisLabel,
            entityObjective,
            showTotalActivities,
            selectedTimeRange
        } = this.props;
        const {
            eCPAActivitiesOrClicks, showActivitiesData, calloutTitle, primaryActivityField, isLabelCoded
        } = getActivitiesSettings(entityObjective);

        const hasCalloutData = get(this.props, 'channel.callouts.params.hasData', false);
        const hasChannelData = get(this.props, 'channel.hasData', false);

        const callouts = get(this.props, 'channel.callouts.data', {});
        const data = get(this.props, 'channel.data', {});
        const calloutWindow = get(this.props, 'channel.callouts.params.timeRanges.previous.windowSize', null);

        const activities = get(this.props, 'activities', {});
        const hasActivities = get(this.props, 'activities.hasData', null);
        const activitiesRenderFlag = hasActivities && (showActivitiesData || showTotalActivities);

        const isChannelAvailable = get(this.props, 'entities.meta.channelAvailability.site', false);

        const hasIOChannelsData = get(this.props, 'siteDirectMeta.hasData', null);
        const ioChannels = get(this.props, 'siteDirectMeta.channels', []);
        const ioChannelData = get(this.props, 'siteDirectMeta.data', {});
        const ioChannelTreeMapData = get(this.props, 'siteDirectMeta.treeMapData', {});
        const ioChannelGroupedData = get(this.props, 'siteDirectMeta.groupedData', {});
        const activeIOChannels = ioChannels.reduce((prev, channel) => {
            // Active Channel is defined to be a channel with a non-zero imprerssion
            // count for a given time window.
            if (ioChannelData[channel].statistics.impressions > 0) {
                prev.push(channel);
            }
            return prev;
        }, []);
        const activeIOChannelsCount = activeIOChannels.length;

        const calloutSettings = [
            { key: 'impressions', title: 'Impressions', format: '0,0' },
            { key: eCPAActivitiesOrClicks, title: calloutTitle, format: '0,0' },
            {
                component: CallOutValueWithTreemap,
                props: {
                    exportName: `${stub}-channels-live`,
                    title: 'Channels Live',
                    data: {
                        value: activeIOChannelsCount,
                        treeMap: ioChannelTreeMapData,
                        channels: activeIOChannels,
                        accessor: 'spend'
                    },
                    enabledChannels: ioChannels,
                    format: 'number'
                },
                hasData: hasIOChannelsData
            }
        ];

        const calloutComponents = generateCalloutComponents({
            stub, calloutSettings, hasCalloutData, callouts, calloutWindow, isLabelCoded
        });

        const upsellSettings = this.props.entityMeta.upsellSettings;
        const upsellRequested = get(this.props, 'entityMeta.upsellRequested', {});

        let ioChannelDisplayComponents = null;
        if (ioChannels.length) {
            const ioChannelsMap = ioChannels.reduce((prev, channel) => {
                if (SiteDirectContainerMap[channel]) {
                    const Component = SiteDirectContainerMap[channel];

                    prev[channel] = <Component key={channel} entityId={this.getCurrentEntityId()} />;
                }

                return prev;
            }, {});

            ioChannelDisplayComponents = orderedChannels.reduce((prev, orderedChannel) => {
                const component = ioChannelsMap[orderedChannel];
                if (component) {
                    prev.push(component);
                }
                return prev;
            }, []);
        }

        const ChannelActions = <React.Fragment>
            <CreateNewNote />
            <ViewEntity />
            {isChannelAvailable && <ExportPDF template={stub} expandSiteDirect />}
        </React.Fragment>;

        return isChannelAvailable ? (
            <Grid fluid>
                <ChannelTitle print={ChannelActions}>
                    Site Direct
                </ChannelTitle>
                {this.props.enableObjectiveSwitching && <ObjectiveSwitcher
                    setObjectiveShadow={this.props.setObjectiveShadow}
                    fetchEntityMeta={this.props.fetchEntityMeta}
                    entityId={this.getCurrentEntityId()}
                    objective={this.props.entityObjective}
                    objectiveLabelCode={this.props.entityObjectiveLabelCode}
                />}
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {/* Call Outs */}
                <Row>
                    {calloutComponents}
                </Row>
                {/* Charts */}
                {hasChannelData ? (
                    <TwoAxisBarWithLineChart
                        title={`IMPRESSIONS VS. ${convertToDisplayValue(eCPAActivitiesOrClicks)}`}
                        exportName={`${stub}-impressions-vs-kpi`}
                        data={data}
                        yBar="impressions"
                        yLine={eCPAActivitiesOrClicks}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        tactic={this.state.channel}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasChannelData} />
                )}

                {hasIOChannelsData ? (
                    <CommonSummaryTable
                        title="SITE DIRECT CHANNEL OVERVIEW"
                        exportName={`${stub}-channel-overview`}
                        keyField="channel"
                        accessors={
                            entityObjective === 'ctr' ?
                                ['impressions', 'clicks', 'ctr', 'spend'] :
                                ['impressions', 'ecpaActivities', 'spend']
                        }
                        hasData={hasIOChannelsData}
                        showTotalActivities={showTotalActivities}
                        data={Object.values(ioChannelData).map(c => c.statistics)} />
                ) : null }

                {activitiesRenderFlag ? (
                    <ActivitiesStackedAreaChart
                        title="ACTIVITY TRENDS"
                        exportName={`${stub}-activity-trends`}
                        exportLimit={10}
                        data={activities.data}
                        keys={activities.rankings[primaryActivityField].map(d => d.name).reverse()}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        yAxisLabel={`${convertToDisplayValue(primaryActivityField)}`}
                        y={primaryActivityField}
                        colors={this.props.theme.orderedColors}
                        animateDuration={2000}
                        categories={activities.categories} />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag} />
                )}

                {hasIOChannelsData ? (
                    <ChannelStackedChart
                        title="IMPRESSIONS BY CHANNEL"
                        exportName={`${stub}-impressions-by-channel`}
                        chart={{ type: 'area', zoomType: 'xy' }}
                        data={ioChannelGroupedData}
                        enabledChannels={ioChannels}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        y="impressions"
                        animateDuration={2000} />
                ) : (
                    <RenderingTile height='400px' show={hasIOChannelsData} />
                )}

                {hasIOChannelsData ? (
                    <ChannelStackedChart
                        title={`${convertToDisplayValue(eCPAActivitiesOrClicks)} BY CHANNEL`}
                        exportName={`${stub}-kpi-by-channel`}
                        chart={{ type: 'area', zoomType: 'xy' }}
                        data={ioChannelGroupedData}
                        enabledChannels={ioChannels}
                        y={eCPAActivitiesOrClicks}
                        yAxisLabel={convertToDisplayValue(eCPAActivitiesOrClicks)}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000} />
                ) : (
                    <RenderingTile height='400px' show={hasIOChannelsData} />
                )}

                {hasIOChannelsData ? (
                    <ChannelStackedChart
                        title='BUDGET BY CHANNEL'
                        exportName={`${stub}-budget-by-channel`}
                        chart={{ type: 'column' }}
                        isMoney={true}
                        data={ioChannelGroupedData}
                        enabledChannels={ioChannels}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        yAxisLabel='Budget'
                        y="spend"
                        animateDuration={2000} />
                ) : (
                    <RenderingTile height='400px' show={hasIOChannelsData} />
                )}

                {ioChannelDisplayComponents}
            </Grid>
        ) : (
            <Grid fluid={true}>
                <ChannelTitle print={ChannelActions}>
                    Site Direct
                </ChannelTitle>
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {upsellSettings.enabled && upsellSettings.site ? (
                    <ChannelUpsell
                        channel='site'
                        channelDisplay='Site Direct'
                        icon='icon-site-fill'
                        successfulRequest={upsellRequested.site}
                        buttonClick={() => {
                            const entityId = this.getCurrentEntityId();
                            const channel = this.getCurrentChannel();
                            return this.props.requestChannelUpsell({ entityId, channel });
                        }}
                        onMount={() => {
                            const entityId = this.getCurrentEntityId();
                            const channel = this.getCurrentChannel();
                            return this.props.showedChannelUpsell({ entityId, channel });
                        }}
                    />
                ) : (
                    <NoChannelDataTile channel='Site Direct' />
                )}
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    theme: state.theme,
    entities: state.entities,
    entityMeta: state.entities.meta,
    siteDirectMeta: state.siteDirect.meta,
    objectiveShadow: state.config.objectiveShadow,
    enableObjectiveSwitching: get(state, 'entities.meta.meta.enableObjectiveSwitching', false),
    entityObjective: get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa'),
    entityObjectiveLabelCode: get(state, 'entities.meta.meta.programmaticObjective.labelCode', null),
    showTotalActivities: shouldShowTotalActivities(state),
    channel: state.channel.site,
    activities: state.activities.site,
    selectedTimeRange: state.config.selectedTimeRange,
    xAxisLabel: get(state, 'config.timeBuckets.config.xAxisLabel', 'Date'),
    backfillBuckets: get(state, 'config.timeBuckets.config.buckets', [])
});

export default connect(mapStateToProps, {
    fetchActivities,
    fetchEntityMeta,
    fetchIOChannels,
    fetchChannel,
    fetchChannelCallouts,
    requestChannelUpsell,
    showedChannelUpsell,
    setObjectiveShadow,
})(Dashboard);



// WEBPACK FOOTER //
// ./src/containers/dashboards/LayerThreeSiteDirectContainer.js